import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import { useSpecialties } from 'data/layout/actions'
import { fetchEmployerUserSignupPostJob } from 'src/redux/employerUserSignup'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'
import GenericCollapsableSection from 'components/Drawers/GenericComponents/GenericCollapsableSection'
import BasicsSection from 'pages/Hospitals/Jobs/sections/BasicsSection'
import LocationSection from 'pages/Hospitals/Jobs/sections/LocationSection'
import CompensationSection from 'pages/Hospitals/Jobs/sections/CompensationSection'
import VisaSection from 'pages/Hospitals/Jobs/sections/VisaSection'
import DescriptionSection from 'pages/Hospitals/Jobs/sections/DescriptionSection'

const NURSE_CANDIDATE_TYPE_ID = 2

const EmployerUserJobFormStep = ({ wizardData, setWizardData, onNext, onBack }) => {
  const dispatch = useDispatch()
  const { postingJob, postJobResult } = useSelector(state => state.employerUserSignup)
  const { specialties, subspecialties, candidateTypes } = useSpecialties()

  const [values, setValues] = useState({
    about: null,
    aboutRaw: null,
    title: null,
    candidateTypeId: null,
    city: null,
    state: null,
    practiceType: null,
    salaryType: null,
    specialtyIds: [],
    subspecialtyIds: [],
    visaIds: [],
    loanForgiveness: null,
    pslfCompatible: null,
    area: null,
    academicType: null,
    shiftType: null,
    salaryMin: null,
    salaryMax: null,
    salary: null,
  })

  const [formSpecialties, setFormSpecialties] = useState([])
  const [formSubspecialties, setFormSubspecialties] = useState([])
  const [showError, setShowError] = useState(false)

  const hasSubmittedRef = useRef(false)
  const hasTrackedFormStartRef = useRef(false)
  const trackedFieldsRef = useRef(new Set())

  useEffect(() => {
    dispatch(
      fetchTrackEvent({
        eventName: 'signup_job_form_viewed',
        eventType: ProductAnalyticsEventTypes.ACTION,
        distinctId: wizardData.email,
      }),
    )
  }, [dispatch, wizardData.email])

  const candidateTypeOptions = candidateTypes.map(({ id: typeId, name }) => ({ id: typeId, name }))

  useEffect(() => {
    if (values.candidateTypeId && specialties.length && candidateTypes.length) {
      const relevantSpecialties = specialties.filter(s =>
        candidateTypes
          .find(ct => ct.id === values.candidateTypeId)
          ?.specialtyIds.includes(s.id),
      )
      setFormSpecialties(relevantSpecialties)
    }
  }, [values.candidateTypeId, specialties, candidateTypes])

  useEffect(() => {
    if (values.specialtyIds.length && subspecialties.length && formSpecialties.length) {
      const updatedSubspecialties = subspecialties.filter(sub =>
        values.specialtyIds.some(id =>
          formSpecialties.some(sp => sp.id === id && sp.subspecialtyIds.includes(sub.id)),
        ),
      )
      setFormSubspecialties(updatedSubspecialties)
    }
  }, [values.specialtyIds, subspecialties, formSpecialties])

  useEffect(() => {
    if (hasSubmittedRef.current && !postingJob && postJobResult) {
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_job_posted',
          eventType: ProductAnalyticsEventTypes.ACTION,
          distinctId: wizardData.email,
          eventData: {
            candidateTypeId: values.candidateTypeId,
            state: values.state,
            matchCounts: postJobResult.matchCounts,
          },
        }),
      )
      setWizardData(prev => ({
        ...prev,
        matchCounts: postJobResult.matchCounts,
        jobId: postJobResult.jobId,
      }))
      onNext()
    }
  }, [postingJob, postJobResult, setWizardData, onNext, dispatch, wizardData.email, values.candidateTypeId, values.state])

  const handleFieldChange = useCallback((name, val) => {
    if (!hasTrackedFormStartRef.current) {
      hasTrackedFormStartRef.current = true
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_job_form_started',
          eventType: ProductAnalyticsEventTypes.ACTION,
          distinctId: wizardData.email,
        }),
      )
    }

    const requiredFields = ['title', 'candidateTypeId', 'practiceType', 'city', 'state']
    if (requiredFields.includes(name) && val && !trackedFieldsRef.current.has(name)) {
      trackedFieldsRef.current.add(name)
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_job_form_field_completed',
          eventType: ProductAnalyticsEventTypes.ACTION,
          distinctId: wizardData.email,
          eventData: { fieldName: name },
        }),
      )
    }

    setValues(prev => ({ ...prev, [name]: val }))
  }, [dispatch, wizardData.email])

  const handleEditorChange = useCallback(({ about, aboutRaw }) => {
    setValues(prev => ({ ...prev, about, aboutRaw }))
  }, [])

  const missingRequiredFields = useMemo(() => {
    const fields = []
    if (!values.title) fields.push('Job Title')
    if (!values.candidateTypeId) fields.push('Profession')
    if (!values.practiceType) fields.push('Practice Type')
    if (!values.city) fields.push('City')
    if (!values.state) fields.push('State')
    return fields
  }, [values])

  const handleSubmit = useCallback(() => {
    if (missingRequiredFields.length > 0) {
      setShowError(true)
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_job_form_validation_error',
          eventType: ProductAnalyticsEventTypes.ACTION,
          distinctId: wizardData.email,
          eventData: { missingFields: missingRequiredFields },
        }),
      )
      return
    }

    const jobData = {
      ...values,
      areaType: values.area,
      shiftType: values.candidateTypeId === NURSE_CANDIDATE_TYPE_ID ? values.shiftType : null,
    }

    hasSubmittedRef.current = true

    dispatch(
      fetchEmployerUserSignupPostJob({
        employerUserId: wizardData.employerUserId,
        job: jobData,
      }),
    )
  }, [values, missingRequiredFields, wizardData.employerUserId, wizardData.email, dispatch])

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: 'success.light',
          border: 1,
          borderColor: 'success.main',
          borderRadius: '12px',
          p: 3,
          mb: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <CardGiftcardIcon sx={{ color: 'success.main', fontSize: 32, mt: 0.25 }} />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant='h6' fontWeight={700}>
              Your first job posting is free
            </Typography>
            <Chip label='No card required' size='small' color='success' variant='outlined' />
          </Box>
          <Typography variant='body2' color='text.secondary'>
            Post your open position at no cost and we'll instantly match you with qualified candidates on the platform.
          </Typography>
        </Box>
      </Box>

      {showError && missingRequiredFields.length > 0 && (
        <Alert severity='error' sx={{ mb: 3 }}>
          Please fill in the following required fields: {missingRequiredFields.join(', ')}
        </Alert>
      )}

      <GenericCollapsableSection title='Basics' isOpenByDefault={true}>
        <BasicsSection
          values={values}
          setState={handleFieldChange}
          showError={showError}
          candidateTypeOptions={candidateTypeOptions}
          formSpecialties={formSpecialties}
          formSubspecialties={formSubspecialties}
          setJobAlertData={() => {}}
        />
      </GenericCollapsableSection>

      <GenericCollapsableSection title='Location' isOpenByDefault={true}>
        <LocationSection
          values={values}
          setState={handleFieldChange}
          showError={showError}
          setJobAlertData={() => {}}
        />
      </GenericCollapsableSection>

      <GenericCollapsableSection title='Compensation & Benefits' isOpenByDefault={true}>
        <CompensationSection
          values={values}
          setState={handleFieldChange}
          showError={showError}
          required={false}
        />
      </GenericCollapsableSection>

      <GenericCollapsableSection title='Visa & Sponsorship' isOpenByDefault={false}>
        <VisaSection
          values={values}
          setState={handleFieldChange}
        />
      </GenericCollapsableSection>

      <GenericCollapsableSection title='Description' isOpenByDefault={false}>
        <DescriptionSection
          values={values}
          onEditorChange={handleEditorChange}
        />
      </GenericCollapsableSection>

      <Box sx={{ display: 'flex', justifyContent: onBack ? 'space-between' : 'flex-end', mt: 4 }}>
        {onBack && (
          <Button onClick={onBack} disabled={postingJob}>
            Back
          </Button>
        )}
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={postingJob}
          startIcon={postingJob ? <CircularProgress size={16} color='inherit' /> : null}
        >
          {postingJob ? 'Posting job...' : 'Post Job'}
        </Button>
      </Box>
    </Box>
  )
}

export default EmployerUserJobFormStep
